import type { ExtendedThemeConfig } from './types';

// Base design tokens that are theme-agnostic
export const BASE_TOKENS = {
  // Spacing
  spaceSm: '8px',
  spaceMd: '16px',
  spaceLg: '20px',

  // Border radius
  radiusLg: '10px',
  radiusMd: '8px',
  radiusSm: '6px',

  // Transitions
  transitionFast: '200ms ease',
  transitionMedium: '300ms ease',
  transitionSlow: '500ms ease',

  // Shadows
  shadowSm: '0 1px 3px rgba(0, 0, 0, 0.1)',
  shadowMd: '0 2px 8px rgba(0, 0, 0, 0.05)',
  shadowLg: '0 4px 12px rgba(0, 0, 0, 0.15)'
};

// Light theme colors
export const LIGHT_COLORS = {
  // Core colors
  primary: '#90BFA0',
  primaryDark: '#306643',

  // Background colors
  containerBg: '#EFEAE7',
  mainBg: '#F8F4F1',
  messageBoxBg: '#FFFFFF',
  assistantBubbleBg: '#FFFFFF',
  assistantOverviewBubbleBg: '#f3f3f3',
  userBubbleBg: '#90BFA0',
  quickPromptBg: '#FFFBF8',

  // Text colors
  mainText: '#000000',
  userBubbleText: '#FFFFFF',
  messageBoxPlaceholder: '#959595',
  assistantBubbleText: '#000000',
  assistantOverviewBubbleText: '#686868',
  quickPromptText: '#5B5B5B',
  sidebarTitleColor: '#000000',
  sidebarGroupColor: '#959595',
  sidebarItemColor: '#323232',

  // Border colors
  messageBoxBorder: '#EDE7E2',
  assistantBubbleBorder: '#EDE7E2',
  quickPromptBorder: '#FBF6F3',

  // Vote colors
  voteLiked: '#008000',
  voteDisliked: '#FF0000',
  voteNeutral: '#999999',

  // Chart-specific colors
  chartText: '#333',
  chartBorder: '#e1e8ed',
  chartTooltipBg: 'rgba(255, 255, 255, 0.95)',
  chartLabel: '#666',
  chartSplitLine: '#f5f5f5'
};

// Dark theme colors
export const DARK_COLORS = {
  // Core colors
  primary: '#306643',
  primaryDark: '#90BFA0',

  // Background colors
  containerBg: '#1D1D1D',
  mainBg: '#292929',
  messageBoxBg: '#383838',
  assistantBubbleBg: '#404040',
  assistantOverviewBubbleBg: '#3f3f3f',
  userBubbleBg: '#306643',
  quickPromptBg: '#515151',

  // Text colors
  mainText: '#EEEEEE',
  userBubbleText: '#FFFFFF',
  messageBoxPlaceholder: '#747474',
  assistantBubbleText: '#FFFFFF',
  assistantOverviewBubbleText: '#949494',
  quickPromptText: '#B5B5B5',
  sidebarTitleColor: '#CCCCCC',
  sidebarGroupColor: '#999999',
  sidebarItemColor: '#BBBBBB',

  // Border colors
  messageBoxBorder: '#595959',
  assistantBubbleBorder: '#595959',
  quickPromptBorder: '#5F5F5F',

  // Vote colors
  voteLiked: '#008000',
  voteDisliked: '#FF0000',
  voteNeutral: '#999999',

  // Chart-specific colors
  chartText: '#EEEEEE',
  chartBorder: '#595959',
  chartTooltipBg: 'rgba(56, 56, 56, 0.95)',
  chartLabel: '#B5B5B5',
  chartSplitLine: '#404040'
};

// Chart color palettes
export const CHART_COLORS = {
  // Default palette for general use
  default: [
    '#5470c6',
    '#91cc75',
    '#fac858',
    '#ee6666',
    '#73c0de',
    '#3ba272',
    '#fc8452',
    '#9a60b4',
    '#ea7ccc'
  ],

  // Categorical data visualization
  categorical: [
    '#5470c6',
    '#91cc75',
    '#fac858',
    '#ee6666',
    '#73c0de',
    '#3ba272',
    '#fc8452',
    '#9a60b4',
    '#ea7ccc',
    '#ffb980',
    '#d87c7c',
    '#919e8b',
    '#d7ab82',
    '#6e7074',
    '#61a0a8'
  ],

  // Sequential data (single hue progression)
  sequential: [
    '#f7fbff',
    '#deebf7',
    '#c6dbef',
    '#9ecae1',
    '#6baed6',
    '#4292c6',
    '#2171b5',
    '#08519c',
    '#08306b'
  ],

  // Diverging data (two contrasting hues)
  diverging: [
    '#67001f',
    '#b2182b',
    '#d6604d',
    '#f4a582',
    '#fddbc7',
    '#f7f7f7',
    '#d1e5f0',
    '#92c5de',
    '#4393c3',
    '#2166ac',
    '#053061'
  ]
};

// Theme configurations
export const LIGHT_THEME: ExtendedThemeConfig = {
  mode: 'light',
  colors: {
    ...LIGHT_COLORS,
    default: CHART_COLORS.default,
    categorical: CHART_COLORS.categorical,
    sequential: CHART_COLORS.sequential,
    diverging: CHART_COLORS.diverging
  },
  tokens: BASE_TOKENS,
  chartColors: CHART_COLORS
};

export const DARK_THEME: ExtendedThemeConfig = {
  mode: 'dark',
  colors: {
    ...DARK_COLORS,
    default: CHART_COLORS.default,
    categorical: CHART_COLORS.categorical,
    sequential: CHART_COLORS.sequential,
    diverging: CHART_COLORS.diverging
  },
  tokens: BASE_TOKENS,
  chartColors: CHART_COLORS
};

// Theme shadow configurations by mode
export const THEME_SHADOWS = {
  light: {
    shadowSm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    shadowMd: '0 2px 8px rgba(0, 0, 0, 0.05)',
    shadowLg: '0 4px 12px rgba(0, 0, 0, 0.15)'
  },
  dark: {
    shadowSm: '0 1px 3px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 2px 8px rgba(0, 0, 0, 0.3)',
    shadowLg: '0 4px 12px rgba(0, 0, 0, 0.4)'
  }
} as const;
